import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // 'dekoration' | 'solfilm' | null
  const [mobileExpanded, setMobileExpanded] = useState({ dekoration: false, solfilm: false });
  const location = useLocation();
  const dropdownTimeoutRef = useRef(null);

  // Luk mobilmenu og dropdowns ved sideskift
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  // Funktion til at tjekke om en rute eller underside er aktiv
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isExactActive = (path) => {
    return location.pathname === path;
  };

  // Håndter hover på desktop med lille timeout så dropdown ikke lukker abrupt
  const handleMouseEnter = (name) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setOpenDropdown(name);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  const dekorationSubmenu = [
    { label: 'Lastbil', href: '/dekoration/lastbil', desc: 'Vognmandsreklamer & lastbiler', icon: 'fa-solid fa-truck' },
    { label: 'Bil', href: '/dekoration/bil', desc: 'Bildekoration & helindpakning', icon: 'fa-solid fa-car' },
    { label: 'Væg', href: '/dekoration/vaeg', desc: 'Vægfolie & indendørs grafik', icon: 'fa-solid fa-paintbrush' }
  ];

  const solfilmSubmenu = [
    { label: 'Bil', href: '/solfilm/bil', desc: 'Solfilm til person- & varebiler', icon: 'fa-solid fa-car-side' },
    { label: 'Maskin', href: '/solfilm/maskin', desc: 'Gravemaskiner & entreprenørmaskiner', icon: 'fa-solid fa-tractor' },
    { label: 'Hus', href: '/solfilm/hus', desc: 'Bygningssolfilm til bolig & erhverv', icon: 'fa-solid fa-house-chimney-window' }
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200/90 bg-white/95 backdrop-blur-md shadow-xs">
      {/* Top accent bar i brandfarve med subtil grøn nuance */}
      <div className="h-1 w-full bg-gradient-to-r from-brand-red via-[#34d399] to-brand-red" />

      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 transition-opacity hover:opacity-90">
          <img
            src="https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/cre8-it-1.png"
            alt="CRE8IT Logo"
            className="h-10 w-auto object-contain sm:h-11"
          />
        </Link>

        {/* Desktop Navigation (Vises kun for skærme >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-1 xl:gap-2">
          {/* Forside */}
          <Link
            to="/"
            className={`px-3 py-2 text-sm font-semibold rounded-lg transition-colors duration-200 ${
              isExactActive('/')
                ? 'text-brand-red bg-brand-green-light/25'
                : 'text-gray-700 hover:text-brand-red hover:bg-gray-50'
            }`}
          >
            Forside
          </Link>

          {/* Dekoration Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter('dekoration')}
            onMouseLeave={handleMouseLeave}
          >
            <button
              type="button"
              onClick={() => setOpenDropdown(openDropdown === 'dekoration' ? null : 'dekoration')}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg transition-colors duration-200 cursor-pointer ${
                isActive('/dekoration')
                  ? 'text-brand-red bg-brand-green-light/25'
                  : 'text-gray-700 hover:text-brand-red hover:bg-gray-50'
              }`}
              aria-expanded={openDropdown === 'dekoration'}
            >
              <span>Dekoration</span>
              <i className={`fa-solid fa-chevron-down text-[11px] transition-transform duration-200 ${openDropdown === 'dekoration' ? 'rotate-180 text-brand-red' : 'text-gray-400'}`} />
            </button>

            {openDropdown === 'dekoration' && (
              <div className="absolute left-0 mt-1 w-64 rounded-2xl border border-gray-200 bg-white p-2 shadow-xl animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Dekorationsopgaver
                </div>
                {dekorationSubmenu.map((sub) => (
                  <Link
                    key={sub.href}
                    to={sub.href}
                    className={`flex items-start gap-3 rounded-xl p-2.5 transition-colors ${
                      isExactActive(sub.href)
                        ? 'bg-brand-green-light/30 text-brand-red'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-brand-red'
                    }`}
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-brand-red">
                      <i className={sub.icon} />
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{sub.label}</div>
                      <div className="text-xs text-gray-500">{sub.desc}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Solfilm Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => handleMouseEnter('solfilm')}
            onMouseLeave={handleMouseLeave}
          >
            <button
              type="button"
              onClick={() => setOpenDropdown(openDropdown === 'solfilm' ? null : 'solfilm')}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg transition-colors duration-200 cursor-pointer ${
                isActive('/solfilm') || isActive('/maskin-bilsolfilm')
                  ? 'text-brand-red bg-brand-green-light/25'
                  : 'text-gray-700 hover:text-brand-red hover:bg-gray-50'
              }`}
              aria-expanded={openDropdown === 'solfilm'}
            >
              <span>Solfilm</span>
              <i className={`fa-solid fa-chevron-down text-[11px] transition-transform duration-200 ${openDropdown === 'solfilm' ? 'rotate-180 text-brand-red' : 'text-gray-400'}`} />
            </button>

            {openDropdown === 'solfilm' && (
              <div className="absolute left-0 mt-1 w-64 rounded-2xl border border-gray-200 bg-white p-2 shadow-xl animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Solfilmsløsninger
                </div>
                {solfilmSubmenu.map((sub) => (
                  <Link
                    key={sub.href}
                    to={sub.href}
                    className={`flex items-start gap-3 rounded-xl p-2.5 transition-colors ${
                      isExactActive(sub.href)
                        ? 'bg-brand-green-light/30 text-brand-red'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-brand-red'
                    }`}
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-brand-red">
                      <i className={sub.icon} />
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{sub.label}</div>
                      <div className="text-xs text-gray-500">{sub.desc}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Interiør folie */}
          <Link
            to="/interioerfolie"
            className={`px-3 py-2 text-sm font-semibold rounded-lg transition-colors duration-200 ${
              isExactActive('/interioerfolie')
                ? 'text-brand-red bg-brand-green-light/25'
                : 'text-gray-700 hover:text-brand-red hover:bg-gray-50'
            }`}
          >
            Interiør folie
          </Link>

          {/* Logo design */}
          <Link
            to="/logodesign"
            className={`px-3 py-2 text-sm font-semibold rounded-lg transition-colors duration-200 ${
              isExactActive('/logodesign')
                ? 'text-brand-red bg-brand-green-light/25'
                : 'text-gray-700 hover:text-brand-red hover:bg-gray-50'
            }`}
          >
            Logo design
          </Link>

          {/* Kontakt */}
          <Link
            to="/kontakt"
            className={`px-3 py-2 text-sm font-semibold rounded-lg transition-colors duration-200 ${
              isExactActive('/kontakt')
                ? 'text-brand-red bg-brand-green-light/25'
                : 'text-gray-700 hover:text-brand-red hover:bg-gray-50'
            }`}
          >
            Kontakt
          </Link>
        </nav>

        {/* Hurtig kontakt på desktop (>= 1200px) */}
        <div className="hidden min-[1200px]:flex items-center gap-4">
          <a
            href="tel:70604788"
            className="flex items-center gap-2 text-sm font-bold text-gray-800 hover:text-brand-red transition-colors whitespace-nowrap"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-green-light/30 text-brand-red">
              <i className="fa-solid fa-phone text-xs" />
            </span>
            <span className="whitespace-nowrap">7060 4788</span>
          </a>
          <Link
            to="/kontakt"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-red px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-red-hover cursor-pointer"
          >
            <i className="fa-solid fa-envelope text-xs" />
            Få et tilbud
          </Link>
        </div>

        {/* Mobil Burgermenu & Ring-knap (Vises ved skærme < 1200px) */}
        <div className="flex items-center gap-2.5 min-[1200px]:hidden">
          <a
            href="tel:70604788"
            className="flex h-10 items-center gap-2 px-3 rounded-xl bg-brand-green-light/30 text-brand-red hover:bg-brand-green-light/50 transition-colors font-bold text-sm"
            aria-label="Ring til 7060 4788"
          >
            <i className="fa-solid fa-phone text-xs" />
            <span className="whitespace-nowrap">7060 4788</span>
          </a>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            aria-label={mobileMenuOpen ? 'Luk menu' : 'Åben menu'}
          >
            <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-lg text-gray-800`} />
          </button>
        </div>
      </div>

      {/* Mobil Menu Overlay (< 1200px) */}
      {mobileMenuOpen && (
        <div className="border-t border-gray-200 bg-white min-[1200px]:hidden max-h-[85vh] overflow-y-auto shadow-lg">
          <div className="space-y-1 px-4 py-4 sm:px-6">
            {/* Forside */}
            <Link
              to="/"
              className={`block rounded-xl px-4 py-3 text-base font-semibold transition-colors ${
                isExactActive('/')
                  ? 'bg-brand-green-light/30 text-brand-red'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-brand-red'
              }`}
            >
              Forside
            </Link>

            {/* Dekoration Accordion */}
            <div className="rounded-xl border border-gray-100 bg-gray-50/60 overflow-hidden">
              <button
                type="button"
                onClick={() => setMobileExpanded(prev => ({ ...prev, dekoration: !prev.dekoration }))}
                className="flex w-full items-center justify-between px-4 py-3 text-base font-semibold text-gray-800 cursor-pointer"
              >
                <span className={isActive('/dekoration') ? 'text-brand-red font-bold' : ''}>Dekoration</span>
                <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${mobileExpanded.dekoration ? 'rotate-180 text-brand-red' : 'text-gray-400'}`} />
              </button>
              {mobileExpanded.dekoration && (
                <div className="border-t border-gray-100 bg-white p-2 space-y-1">
                  {dekorationSubmenu.map((sub) => (
                    <Link
                      key={`mobile-${sub.href}`}
                      to={sub.href}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ${
                        isExactActive(sub.href)
                          ? 'bg-brand-green-light/30 text-brand-red font-semibold'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-brand-red'
                      }`}
                    >
                      <i className={`${sub.icon} text-brand-red w-5 text-center`} />
                      <div>
                        <div>{sub.label}</div>
                        <div className="text-xs text-gray-400 font-normal">{sub.desc}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Solfilm Accordion */}
            <div className="rounded-xl border border-gray-100 bg-gray-50/60 overflow-hidden">
              <button
                type="button"
                onClick={() => setMobileExpanded(prev => ({ ...prev, solfilm: !prev.solfilm }))}
                className="flex w-full items-center justify-between px-4 py-3 text-base font-semibold text-gray-800 cursor-pointer"
              >
                <span className={isActive('/solfilm') || isActive('/maskin-bilsolfilm') ? 'text-brand-red font-bold' : ''}>Solfilm</span>
                <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${mobileExpanded.solfilm ? 'rotate-180 text-brand-red' : 'text-gray-400'}`} />
              </button>
              {mobileExpanded.solfilm && (
                <div className="border-t border-gray-100 bg-white p-2 space-y-1">
                  {solfilmSubmenu.map((sub) => (
                    <Link
                      key={`mobile-${sub.href}`}
                      to={sub.href}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ${
                        isExactActive(sub.href)
                          ? 'bg-brand-green-light/30 text-brand-red font-semibold'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-brand-red'
                      }`}
                    >
                      <i className={`${sub.icon} text-brand-red w-5 text-center`} />
                      <div>
                        <div>{sub.label}</div>
                        <div className="text-xs text-gray-400 font-normal">{sub.desc}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Interiør folie */}
            <Link
              to="/interioerfolie"
              className={`block rounded-xl px-4 py-3 text-base font-semibold transition-colors ${
                isExactActive('/interioerfolie')
                  ? 'bg-brand-green-light/30 text-brand-red'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-brand-red'
              }`}
            >
              Interiør folie
            </Link>

            {/* Logo design */}
            <Link
              to="/logodesign"
              className={`block rounded-xl px-4 py-3 text-base font-semibold transition-colors ${
                isExactActive('/logodesign')
                  ? 'bg-brand-green-light/30 text-brand-red'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-brand-red'
              }`}
            >
              Logo design
            </Link>

            {/* Kontakt */}
            <Link
              to="/kontakt"
              className={`block rounded-xl px-4 py-3 text-base font-semibold transition-colors ${
                isExactActive('/kontakt')
                  ? 'bg-brand-green-light/30 text-brand-red'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-brand-red'
              }`}
            >
              Kontakt
            </Link>

            {/* Hurtig-knapper i mobilmenu */}
            <div className="pt-4 border-t border-gray-100 mt-4 space-y-2.5">
              <Link
                to="/kontakt"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-red py-3 text-center text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-red-hover"
              >
                <i className="fa-solid fa-paper-plane text-xs" />
                Send os en forespørgsel
              </Link>
              <a
                href="https://www.google.com/maps/dir//''/data=!4m7!4m6!1m1!4e2!1m2!1m1!1s0x464b45024e9491d5:0x36f4b91c482f1678!3e0"
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 py-3 text-center text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100"
              >
                <i className="fa-solid fa-route text-brand-red text-xs" />
                Find vej til værkstedet
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
